import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useOffers, type OfferWithCustomer } from "@/hooks/use-offers-queries";
import {
  weightedPipeline,
  bySeller,
  bucketFor,
  type PipelineOffer,
  type ProbabilityWeights,
  type WeightedPipelineResult,
  type SellerPipelineResult,
  type PipelineBucket,
} from "@/lib/offer-pipeline-calc";

/**
 * Placeholder-startvekter (VIB-1304, Erik 15.07) — identisk med seed-
 * migrasjonen 20260718130801_offer_pipeline_config_seed.sql. Brukes KUN som
 * fallback hvis system_config-raden mangler (bør aldri skje etter seed, men
 * hooken skal aldri returnere en tom/ugyldig mapping). Den ENE stedet dette
 * hardkodes — kalibrering (VIB-1312) skjer via UPDATE på system_config-raden,
 * ikke her.
 * TODO(VIB-1312): byttes til empiriske vinnrater når ~6 mnd data foreligger.
 */
export const FALLBACK_WEIGHTS: ProbabilityWeights = { low: 0.25, likely: 0.55, very_likely: 0.85 };

/**
 * Parser en rå system_config.value til ProbabilityWeights, med per-nøkkel
 * fallback (aldri NaN/undefined). Utskilt som ren funksjon for testbarhet
 * uten å måtte mocke Supabase/React Query.
 */
export function parseOfferProbabilityWeights(value: unknown): ProbabilityWeights {
  const v = value as Partial<ProbabilityWeights> | null | undefined;
  if (!v || typeof v !== "object") return FALLBACK_WEIGHTS;
  return {
    low: Number.isFinite(v.low) ? (v.low as number) : FALLBACK_WEIGHTS.low,
    likely: Number.isFinite(v.likely) ? (v.likely as number) : FALLBACK_WEIGHTS.likely,
    very_likely: Number.isFinite(v.very_likely) ? (v.very_likely as number) : FALLBACK_WEIGHTS.very_likely,
  };
}

/**
 * Leser offer_probability_weights fra system_config. queryKey
 * ["system-config", "offer_probability_weights"] matcher det generiske
 * entityField-mønsteret allerede registrert i CACHE_REGISTRY under
 * system_config (src/lib/cache-registry/registry.ts) — ingen egen entry
 * nødvendig.
 */
export function useOfferPipelineWeights() {
  return useQuery({
    queryKey: ["system-config", "offer_probability_weights"],
    queryFn: async (): Promise<ProbabilityWeights> => {
      const { data, error } = await supabase
        .from("system_config")
        .select("value")
        .eq("key", "offer_probability_weights")
        .maybeSingle();
      if (error) throw error;
      return parseOfferProbabilityWeights(data?.value);
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function toPipelineOffer(o: OfferWithCustomer): PipelineOffer {
  return {
    id: o.id,
    status: o.status,
    probability: o.probability,
    valid_until: o.valid_until,
    total_amount: o.total_amount,
    created_by: o.created_by,
    writers: o.writers,
  };
}

export interface OfferPipelineData {
  offers: OfferWithCustomer[];
  weights: ProbabilityWeights;
  today: Date;
  /** Team-total vektet pipeline (kun gyldige sent-tilbud). */
  total: WeightedPipelineResult;
  /** Per-selger vektet pipeline (writers-attribusjon, fallback created_by). */
  bySeller: Map<string, SellerPipelineResult>;
  /** Tilbud gruppert per board-bøtte (draft/sent/expired/accepted/lost). */
  groups: Record<PipelineBucket, OfferWithCustomer[]>;
  isLoading: boolean;
}

/**
 * Aggregert pipeline-data for KPI-kortet og kanban-boardet (VIB-1304). Leser
 * offers via den eksisterende `useOffers()` (samme filtrerte kilde som
 * OffersPage — inkl. TEST_USER_EMAIL-filteret, jf. endringssjekk "Skal IKKE
 * røres") + vektene fra system_config. All matte skjer i
 * src/lib/offer-pipeline-calc.ts (ren, testet).
 */
export function useOfferPipeline(): OfferPipelineData {
  const { data: offers = [], isLoading: offersLoading } = useOffers();
  const { data: weights = FALLBACK_WEIGHTS, isLoading: weightsLoading } = useOfferPipelineWeights();
  const today = new Date();

  const pipelineOffers = offers.map(toPipelineOffer);
  const total = weightedPipeline(pipelineOffers, weights, today);
  const sellerMap = bySeller(pipelineOffers, weights, today);

  // Boardet trenger de fulle OfferWithCustomer-radene (kunde, creator) for
  // kortene, ikke bare PipelineOffer — bruk bucketFor() (samme avledningsregel
  // som groupByStatus) per rad i stedet for å duplisere grupperingslogikken.
  const groups: Record<PipelineBucket, OfferWithCustomer[]> = {
    draft: [],
    sent: [],
    expired: [],
    accepted: [],
    lost: [],
  };
  for (const o of offers) {
    groups[bucketFor(o, today)].push(o);
  }

  return {
    offers,
    weights,
    today,
    total,
    bySeller: sellerMap,
    groups,
    isLoading: offersLoading || weightsLoading,
  };
}
